import React from 'react';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Alert, Select } from 'antd';

import config from '@sp/config';

import {
  requiredPhone,
  optionalPhone,
} from '../../../../utils/form/validation/phone';
import s from './styles.less';

const { Option } = Select;

const renderInput = ({
  input,
  label,
  type,
  disabled,
  className,
  meta: { touched, error },
}) => (
  <div className={s.form_row}>
    <label
      className={`${className || ''} ${touched && error ? s.error_color : ''}`}
      htmlFor={input.name}
    >
      {label}
    </label>
    <div>
      <input
        className={`${s.input} ${touched && error ? s.input_invalid : ''}`}
        {...input}
        type={type}
        id={input.name}
        disabled={disabled}
      />
      {touched && error && <span className={s.error}>{error}</span>}
    </div>
  </div>
);
renderInput.propTypes = {
  input: PropTypes.object,
  meta: PropTypes.object,
  label: PropTypes.string,
  type: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

export const renderCheckBox = ({
  input,
  label,
  type,
  meta: { touched, error },
}) => (
  <div className={s.checkBox_row}>
    <input {...input} id={input.name} type={type} />
    <label
      className={touched && error ? s.error_color : ''}
      htmlFor={input.name}
    >
      {label}
    </label>
    {touched && error && <div className={s.error}>{error}</div>}
  </div>
);

renderCheckBox.propTypes = {
  input: PropTypes.object,
  label: PropTypes.any,
  type: PropTypes.string,
  meta: PropTypes.object,
};

class Form extends React.Component {
  static propTypes = {
    handleSubmit: PropTypes.func,
    change: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.any,
    match: PropTypes.object,
    params: PropTypes.object,
    Package: PropTypes.shape({
      carsNumber: PropTypes.number,
    }),
    fields: PropTypes.any,
  };

  static validateNotBlank(val) {
    if (!val) {
      return 'This field is required.';
    }
    return undefined;
  }

  static mandatoryCheckbox(val) {
    if (!val) {
      return 'You must confirm this';
    }
    return undefined;
  }

  static upperCase(data) {
    if (data) {
      return data.toUpperCase();
    }
    return data;
  }

  state = {
    changesDisabled: true,
    options: [],
    options2: [],
    showSelect: false,
    selectValue: '',
    select2Value: '',
    id: this.props.match.params.id,
  };

  mandatoryCheckboxes = [
    // eslint-disable-next-line prettier/prettier
    `I have explained details of the ${
      config.SPProviderName
    } Service Plan to the customer, and have explained what is covered and what is not covered under the plan.`,
    // eslint-disable-next-line prettier/prettier
    `I can confirm that the customer understands that he/she should make all service bookings via the ${
      config.SPProviderName
    } customer Service Plan portal at least 14 days in advance of their desired booking date.`,
    <span>
      I confirm that the customer is happy to proceed with the purchase of the
      Service Plan and is happy with the Service Plan{' '}
      <a
        href="/docs/terms-conditions.pdf"
        target="_blank"
        className={s.tc}
        download="Terms And Conditions.pdf"
        rel="noreferrer noopener"
      >
        Terms and Conditions.
      </a>
    </span>,
  ];

  async componentDidMount() {
    const customerData = await fetch(
      `/provider/customer-details/${this.state.id}`,
    );
    Object.entries(customerData).forEach(x => {
      this.props.change(x[0], x[1]);
    });
    if (!customerData.phone && !customerData.mobilePhone) {
      this.setState({
        changesDisabled: false,
      });
    }
    if (!customerData.address1) {
      this.setState({
        showSelect: true,
      });
    }
  }

  onSelectChange = async address => {
    this.setState({
      selectValue: address,
    });
    const options = await this.fetchLocations(address);

    this.setState({
      options,
    });
  };

  onSelect2Change = async address => {
    this.setState({
      select2Value: address,
    });
    const options = await this.fetchLocations(address);

    this.setState({
      options2: options,
    });
  };

  setLocationDetails = (
    location,
    {
      postcode = 'postcode',
      city = 'city',
      addressLine1 = 'addressLine1',
      addressLine2 = 'addressLine2',
    } = {},
  ) => {
    const { change } = this.props;

    change(postcode, location.PostalCode);
    change(city, location.City);
    change(addressLine1, location.Line1);
    change(addressLine2, location.Line2 || '');
  };

  fetchLocationDetails = locationId =>
    fetch(`/locations/${locationId}/details`);

  toggleChanges = e => {
    this.setState({
      changesDisabled: !e.target.checked,
    });
  };

  fetchLocations = async address => {
    const options = await fetch(`/locations/search/${address}`);

    return options.map(({ place_id: placeId, description }) => ({
      value: placeId,
      text: description,
    }));
  };

  selectAddress = async value => {
    this.setState({
      selectValue: '',
    });
    const details = await this.fetchLocationDetails(value);
    this.setLocationDetails(details);
  };

  select2Address = async value => {
    this.setState({
      select2Value: '',
    });
    const details = await this.fetchLocationDetails(value);
    this.setLocationDetails(details, {
      postcode: 'Customer2.postcode',
      city: 'Customer2.city',
      addressLine1: 'Customer2.addressLine1',
      addressLine2: 'Customer2.addressLine2',
    });
  };

  submit = async values => {
    try {
      await fetch(`/validate-customer-email/${values.email}`);
    } catch (e) {
      throw new SubmissionError({ email: e.error.toString() });
    }

    try {
      await fetch(`${config.apiUrl}/api/location/postcode/check`, {
        method: 'post',
        body: { postcode: values.postcode },
      });
    } catch (e) {
      throw new SubmissionError({
        postcode: /Postcode isn't valid/.test(e.error)
          ? 'Postcode not valid'
          : e.error,
      });
    }

    try {
      const data = await fetch(`/provider/submit-quote/${this.state.id}`, {
        method: 'post',
        body: values,
      });
      window.location.href = data.redirectUrl;
    } catch (e) {
      throw new SubmissionError({ _error: e.error.toString() });
    }
  };

  copyCustomer1 = () => {
    [
      'firstName',
      'lastName',
      'email',
      'phone',
      'mobilePhone',
      'city',
      'postcode',
      'addressLine1',
      'addressLine2',
    ].forEach(x => this.props.change(`Customer2.${x}`, this.props.fields[x]));
  };

  render() {
    return (
      <form onSubmit={this.props.handleSubmit(this.submit)}>
        {this.props.Package.carsNumber === 2 && (
          <h1 className={s.center}>First Car</h1>
        )}
        <h4 className={s.title}>CUSTOMER DETAILS</h4>
        <br />
        {this.state.showSelect && (
          <div className={s.form_row}>
            <label htmlFor="select">Start typing address</label>
            <div>
              <Select
                mode="combobox"
                notFoundContent=""
                name="select"
                className={s.select}
                filterOption={false}
                placeholder="Town or Postcode"
                value={this.state.selectValue}
                onSelect={this.selectAddress}
                onSearch={this.onSelectChange}
                style={{ width: '100%' }}
                disabled={this.state.changesDisabled}
              >
                {this.state.options.map(x => (
                  <Option key={x.text} disabled={x.disabled} value={x.value}>
                    {x.text}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        )}
        <Field
          className={s.mandatory}
          name="addressLine1"
          validate={this.constructor.validateNotBlank}
          label="Address line 1"
          component={renderInput}
          disabled={this.state.changesDisabled}
        />
        <Field
          name="addressLine2"
          label="Address line 2"
          component={renderInput}
          disabled={this.state.changesDisabled}
        />
        <Field
          className={s.mandatory}
          name="city"
          validate={this.constructor.validateNotBlank}
          label="City"
          component={renderInput}
          disabled={this.state.changesDisabled}
        />
        <Field
          className={s.mandatory}
          name="postcode"
          validate={this.constructor.validateNotBlank}
          label="Postcode"
          normalize={this.constructor.upperCase}
          component={renderInput}
          disabled={this.state.changesDisabled}
        />
        <Field
          className={s.mandatory}
          name="email"
          validate={this.constructor.validateNotBlank}
          label="Customer Email"
          component={renderInput}
          disabled={this.state.changesDisabled}
          type="email"
        />
        <Field
          className={s.mandatory}
          name="firstName"
          validate={this.constructor.validateNotBlank}
          label="Customer First Name"
          component={renderInput}
          disabled={this.state.changesDisabled}
        />
        <Field
          className={s.mandatory}
          name="lastName"
          validate={this.constructor.validateNotBlank}
          label="Customer Last Name"
          component={renderInput}
          disabled={this.state.changesDisabled}
        />
        <Field
          className={s.mandatory}
          name="mobilePhone"
          validate={requiredPhone}
          label="Customer Mobile Phone"
          component={renderInput}
          disabled={this.state.changesDisabled}
        />
        <Field
          name="phone"
          validate={optionalPhone}
          label="Phone"
          component={renderInput}
          disabled={this.state.changesDisabled}
        />
        <div className={s.form_row}>
          <div />
          <label>
            <input
              type="checkbox"
              value={!this.state.changesDisabled}
              onChange={this.toggleChanges}
            />
            <span className={s.label}>
              &nbsp;&nbsp;Tick this box to change customer details, this will
              amend all service plans held for this customer.
            </span>
          </label>
        </div>
        {this.props.Package.carsNumber === 2 && (
          <div>
            <h1 className={s.center}>Second Car</h1>
            <br />
            <div className={s.form_row}>
              <div />
              <label>
                <input onChange={this.copyCustomer1} type="checkbox" />
                &nbsp; &nbsp;
                <span className={s.label}>Same as above</span>
              </label>
            </div>
            <div className={s.form_row}>
              <label htmlFor="select">Start typing address</label>
              <div>
                <Select
                  mode="combobox"
                  notFoundContent=""
                  name="select"
                  className={s.select}
                  filterOption={false}
                  placeholder="Town or Postcode"
                  value={this.state.select2Value}
                  onSelect={this.select2Address}
                  onSearch={this.onSelect2Change}
                  style={{ width: '100%' }}
                >
                  {this.state.options2.map(x => (
                    <Option key={x.text} disabled={x.disabled} value={x.value}>
                      {x.text}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
            <Field
              className={s.mandatory}
              name="Customer2.addressLine1"
              validate={this.constructor.validateNotBlank}
              label="Address line 1"
              component={renderInput}
            />
            <Field
              name="Customer2.addressLine2"
              label="Address line 2"
              component={renderInput}
            />
            <Field
              className={s.mandatory}
              name="Customer2.city"
              validate={this.constructor.validateNotBlank}
              label="City"
              component={renderInput}
            />
            <Field
              className={s.mandatory}
              name="Customer2.postcode"
              validate={this.constructor.validateNotBlank}
              label="Postcode"
              normalize={this.constructor.upperCase}
              component={renderInput}
            />
            <Field
              className={s.mandatory}
              name="Customer2.email"
              validate={this.constructor.validateNotBlank}
              label="Customer Email"
              component={renderInput}
              type="email"
            />
            <Field
              className={s.mandatory}
              name="Customer2.firstName"
              validate={this.constructor.validateNotBlank}
              label="Customer First Name"
              component={renderInput}
            />
            <Field
              className={s.mandatory}
              name="Customer2.lastName"
              validate={this.constructor.validateNotBlank}
              label="Customer Last Name"
              component={renderInput}
            />
            <Field
              className={s.mandatory}
              validate={optionalPhone}
              name="Customer2.mobilePhone"
              label="Customer Mobile Phone"
              component={renderInput}
            />
            <Field
              validate={optionalPhone}
              name="Customer2.phone"
              label="Phone"
              component={renderInput}
            />
            <br />
          </div>
        )}
        <hr />
        <h4 className={s.title}>Garage Declaration:</h4>
        <br />
        {this.mandatoryCheckboxes.map((c, key) => (
          <Field
            key={key}
            name={`garageDeclaration${key + 1}`}
            type="checkbox"
            component={renderCheckBox}
            label={c}
            validate={this.constructor.mandatoryCheckbox}
          />
        ))}
        <br />
        <button
          type="submit"
          className={s.button}
          disabled={this.props.disabled}
        >
          Complete
        </button>
        {this.props.error && (
          <div className={s.error_box}>
            <Alert type="error" message={this.props.error} />
          </div>
        )}
      </form>
    );
  }
}

const formName = 'book-quote';
const Wrapped = reduxForm({
  form: formName,
})(Form);
export default withRouter(
  connect(state => ({
    fields: state.form[formName] ? state.form[formName].values : {},
  }))(Wrapped),
);
